import { ChevronDown, ChevronUp, FileText, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  canPortalSubmitNf,
  formatCompetenciaLabel,
  formatPjNfPaymentLabel,
  PJ_NF_STATUS_CLASS,
  PJ_NF_STATUS_LABELS,
} from '@/components/PjNf/pjNfStatus'
import { usePortalPjNfPage } from '@/hooks/usePortalPjNfPage'
import type { PjNfMensalRow, PjNfParsePreview } from '@/services/pjNfMensalService'
import { cn } from '@/utils/cn'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { formatCnpj } from '@/components/PjAgrada/pjAgradaFormat'

function NfStatusBadge({ status }: { status: PjNfMensalRow['status'] }) {
  return (
    <span
      className={cn(
        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
        PJ_NF_STATUS_CLASS[status],
      )}
    >
      {PJ_NF_STATUS_LABELS[status]}
    </span>
  )
}

function ParsePreviewPanel({ preview, parsing }: { preview: PjNfParsePreview | null; parsing: boolean }) {
  if (parsing) {
    return (
      <div className='rounded-lg border border-white/10 bg-white/[0.05] p-3 text-sm text-white/65'>
        Lendo arquivo e validando NF…
      </div>
    )
  }

  if (!preview) return null

  const { parsed } = preview
  const emissaoLabel = parsed.dataEmissao
    ? new Date(parsed.dataEmissao).toLocaleDateString('pt-BR')
    : '—'

  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm',
        preview.valid
          ? 'border-[#e91e63]/25 bg-[#e91e63]/10'
          : preview.canSubmitWithRessalvas
            ? 'border-amber-500/30 bg-amber-500/10'
            : 'border-red-500/25 bg-red-500/10',
      )}
    >
      {preview.valid ? (
        <>
          <p className='font-medium text-[#ff7ab3]'>NF identificada no arquivo</p>
          <ul className='mt-2 space-y-1 text-white/65'>
            <li>Número: {parsed.numeroNf ?? '—'}</li>
            <li>Emissão: {emissaoLabel}</li>
            <li>
              CNPJ emissor:{' '}
              {parsed.cnpjEmissor ? formatCnpj(parsed.cnpjEmissor) : '—'}
            </li>
            <li className='text-xs uppercase tracking-wide text-white/45'>
              Fonte: {parsed.source === 'xml' ? 'XML' : parsed.source === 'pdf' ? 'PDF' : 'desconhecida'}
            </li>
          </ul>
        </>
      ) : preview.canSubmitWithRessalvas ? (
        <>
          <p className='font-medium text-amber-200'>Divergências encontradas</p>
          <ul className='mt-2 list-disc space-y-1 pl-4 text-white/65'>
            {preview.ressalvas.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className='mt-3 text-xs text-amber-100/80'>
            Você pode enviar mesmo assim. O financeiro será alertado para revisar antes de validar.
          </p>
          <ul className='mt-2 space-y-1 text-white/65'>
            <li>Número lido: {parsed.numeroNf ?? '—'}</li>
            <li>Emissão lida: {emissaoLabel}</li>
            <li>
              CNPJ lido: {parsed.cnpjEmissor ? formatCnpj(parsed.cnpjEmissor) : '—'}
            </li>
          </ul>
        </>
      ) : (
        <>
          <p className='font-medium text-red-300'>Arquivo não validado</p>
          <ul className='mt-2 list-disc space-y-1 pl-4 text-white/65'>
            {preview.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </>
      )}
      {preview.warnings.length > 0 && (
        <ul className='mt-2 list-disc space-y-1 pl-4 text-xs text-amber-100/80'>
          {preview.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function UploadForm({
  file,
  onFileChange,
  parsePreview,
  parsing,
  canSubmit,
  needsRessalvaConfirm,
  submitting,
  onSubmit,
  onCancel,
  rejeicaoMotivo,
}: {
  file: File | null
  onFileChange: (file: File | null) => void
  parsePreview: PjNfParsePreview | null
  parsing: boolean
  canSubmit: boolean
  needsRessalvaConfirm: boolean
  submitting: boolean
  onSubmit: () => void
  onCancel: () => void
  rejeicaoMotivo?: string | null
}) {
  return (
    <div className='mt-4 rounded-lg border border-white/10 bg-white/[0.04] p-4'>
      {rejeicaoMotivo && (
        <div className='mb-4 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-sm'>
          <p className='font-medium text-red-300'>Motivo da rejeição</p>
          <p className='mt-1 text-white/65'>{rejeicaoMotivo}</p>
        </div>
      )}
      <form
        className='space-y-4'
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <label className='block text-sm'>
          <span className='mb-1 block text-white/70'>Arquivo da NF (PDF ou XML)</span>
          <p className='mb-2 text-xs text-white/50'>
            O sistema lê número, data de emissão e CNPJ do emissor automaticamente. XML é mais
            confiável que PDF.
          </p>
          <input
            type='file'
            accept='application/pdf,.pdf,application/xml,text/xml,.xml'
            className='block w-full text-sm text-white/65 file:mr-3 file:rounded-md file:border-0 file:bg-[#e91e63]/20 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white'
            onChange={(e) => void onFileChange(e.target.files?.[0] ?? null)}
            required
          />
          {file && <p className='mt-2 text-xs text-white/45'>{file.name}</p>}
        </label>

        <ParsePreviewPanel preview={parsePreview} parsing={parsing} />

        <div className='flex flex-wrap gap-2'>
          <Button
            type='submit'
            variant='primary'
            icon={<Upload size={16} />}
            disabled={!canSubmit || submitting || parsing}
          >
            {submitting ? 'Enviando…' : needsRessalvaConfirm ? 'Enviar com ressalvas' : 'Enviar NF'}
          </Button>
          <Button type='button' variant='secondary' onClick={onCancel} disabled={submitting || parsing}>
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  )
}

export function PortalPjNfSection() {
  const {
    linkStatus,
    rows,
    loading,
    isPjOnly,
    competenciaAtual,
    currentRow,
    uploadRowId,
    uploadRow,
    openUpload,
    closeUpload,
    file,
    handleFileChange,
    parsePreview,
    parsing,
    canSubmit,
    needsRessalvaConfirm,
    submitting,
    submit,
  } = usePortalPjNfPage(true)

  if (loading) {
    return (
      <div className='flex justify-center py-20'>
        <div className='h-9 w-9 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  if (!linkStatus?.linked) {
    return (
      <div className='rounded-xl border border-amber-400/25 bg-amber-400/10 p-6 text-sm text-amber-50'>
        <p className='font-semibold text-amber-200'>Cadastro PJ não vinculado</p>
        <p className='mt-2 text-amber-50/80'>
          {isPjOnly
            ? 'Seu usuário ainda não está associado a um prestador PJ. Entre em contato com o financeiro para concluir o vínculo antes de enviar notas fiscais.'
            : 'Seu login ainda não está vinculado a um cadastro PJ. Peça ao financeiro para associar seu usuário no cadastro PJ Rocket (campo “Usuário do portal”).'}
        </p>
      </div>
    )
  }

  const inicioLabel = linkStatus.dataInicioContrato
    ? formatCompetenciaLabel(linkStatus.dataInicioContrato.slice(0, 7))
    : null

  return (
    <div className='space-y-6'>
      <div className='rounded-xl border border-white/10 bg-white/[0.05] p-5 shadow-sm'>
        <p className='text-xs font-semibold uppercase tracking-wide text-white/45'>Prestador</p>
        <h3 className='mt-1 text-lg font-semibold text-white'>{linkStatus.nomeCompleto}</h3>
        <p className='mt-2 text-sm text-white/65'>
          {inicioLabel
            ? `Competências disponíveis desde ${inicioLabel}. Envie a NF de cada mês até o dia 5 para liberação do pagamento.`
            : 'Envie a NF de cada mês de referência até o dia 5 para liberação do pagamento.'}
        </p>
        {currentRow && (
          <div className='mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#e91e63]/25 bg-[#e91e63]/10 px-4 py-3 text-sm'>
            <span className='font-medium text-white'>
              Mês atual: {formatCompetenciaLabel(competenciaAtual)}
            </span>
            <NfStatusBadge status={currentRow.status} />
            <span className='text-white/65'>{formatPjNfPaymentLabel(currentRow)}</span>
          </div>
        )}
      </div>

      <div>
        <h4 className='mb-3 text-sm font-semibold text-white'>Notas fiscais por mês</h4>
        {rows.length === 0 ? (
          <div className='rounded-xl border border-white/10 bg-white/[0.05] p-6 text-sm text-white/65'>
            Nenhuma competência disponível ainda.
          </div>
        ) : (
          <ul className='space-y-3'>
            {rows.map((row) => {
              const isCurrent = row.competencia === competenciaAtual
              const isOpen = uploadRowId === row.id
              const canUpload = canPortalSubmitNf(row.status)

              return (
                <li
                  key={row.id}
                  className={cn(
                    'rounded-xl border bg-white/[0.05]',
                    isCurrent
                      ? 'border-[#e91e63]/30 shadow-sm'
                      : 'border-white/10',
                  )}
                >
                  <div className='flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='min-w-0'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='font-semibold text-white'>
                          {formatCompetenciaLabel(row.competencia)}
                        </span>
                        {isCurrent && (
                          <span className='rounded-full bg-[#e91e63]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#ff8fbd]'>
                            Atual
                          </span>
                        )}
                      </div>
                      <p className='mt-1 text-sm text-white/65'>{formatPjNfPaymentLabel(row)}</p>
                      {row.numeroNf && (
                        <p className='mt-1 text-xs text-white/45'>NF nº {row.numeroNf}</p>
                      )}
                    </div>

                    <div className='flex flex-wrap items-center gap-2 sm:justify-end'>
                      <NfStatusBadge status={row.status} />
                      {row.nfFileUrl && (
                        <a
                          href={resolvePublicAssetUrl(row.nfFileUrl)}
                          target='_blank'
                          rel='noreferrer'
                          className='inline-flex items-center gap-1 text-sm font-medium text-[#ff8fbd] hover:underline'
                        >
                          <FileText size={15} />
                          Ver NF
                        </a>
                      )}
                      {canUpload && (
                        <Button
                          type='button'
                          variant={isOpen ? 'secondary' : 'primary'}
                          size='sm'
                          icon={isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          onClick={() => (isOpen ? closeUpload() : openUpload(row))}
                        >
                          {row.status === 'REJEITADA' ? 'Reenviar NF' : 'Enviar NF'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isOpen && uploadRow?.id === row.id && (
                    <div className='border-t border-white/10 px-4 pb-4'>
                      <UploadForm
                        file={file}
                        onFileChange={handleFileChange}
                        parsePreview={parsePreview}
                        parsing={parsing}
                        canSubmit={canSubmit}
                        needsRessalvaConfirm={needsRessalvaConfirm}
                        submitting={submitting}
                        onSubmit={() => void submit()}
                        onCancel={closeUpload}
                        rejeicaoMotivo={row.rejeicaoMotivo}
                      />
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
